/**
 * The listicle hub: six "best X" pages, five languages, one shape.
 *
 * ── Why `/best/<slug>` and not `/best-<slug>` ────────────────────────────────
 *
 * The keyword is "best looksmaxxing apps" and the tempting URL is
 * `/best-looksmaxxing-apps`, one segment, keyword intact. It cannot go there:
 * a single root segment collides with `/$lang` exactly as `/fazly-vs-umax` did,
 * and `lib/compare/index.ts` records that diagnosis in full. `/best/<slug>`
 * keeps the whole keyword in the path, one segment deeper, with no collision.
 *
 * ── Why these are not part of `lib/compare` ──────────────────────────────────
 *
 * They share a research finding and nothing else. A comparison page ends a
 * decision between two named things; a listicle answers "what are my options",
 * which is an earlier question from a reader who has not chosen the two things
 * yet. Merging them would mean one type carrying both a rival and an entry
 * list, and every page rendering half of it.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

import { en } from "./en";
import type { BestCopy, BestSlug } from "./types";
import { BEST_SLUGS } from "./types";

export function bestPath(slug: BestSlug, lang: ContentLang = "en"): string {
  return lang === "en" ? `/best/${slug}` : `/${lang}/best/${slug}`;
}

export function bestIndexPath(lang: ContentLang = "en"): string {
  return lang === "en" ? "/best" : `/${lang}/best`;
}

export function bestSlugOf(raw: string): BestSlug | null {
  return (BEST_SLUGS as readonly string[]).includes(raw)
    ? (raw as BestSlug)
    : null;
}

const LOADERS: Record<ContentLang, () => Promise<Record<BestSlug, BestCopy>>> =
  {
    en: async () => en,
    de: () => import("./de").then((m) => m.de),
    tr: () => import("./tr").then((m) => m.tr),
    pt: () => import("./pt").then((m) => m.pt),
    es: () => import("./es").then((m) => m.es),
  };

export function loadBestCopy(
  lang: ContentLang,
): Promise<Record<BestSlug, BestCopy>> {
  return LOADERS[lang]();
}

export type { BestCopy, BestSlug };
export { BEST_SLUGS };
